// src/scenes/GestionStock/AjusterStockModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { inventoryService } from '../../services/inventory';

const AjusterStockModal = ({ open, onClose, stock, onSuccess }) => {
  const [typeMouvement, setTypeMouvement] = useState('entree');
  const [quantite, setQuantite] = useState('');
  const [motif, setMotif] = useState('');
  const [stockMinimum, setStockMinimum] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ✅ Par défaut désactivé : l'utilisateur active seulement s'il veut bouger le stock
  const [avecMouvement, setAvecMouvement] = useState(false);

  // États conditionnements
  const [conditionnements, setConditionnements] = useState([]);
  const [currentConditionnement, setCurrentConditionnement] = useState({
    unite: '',
    quantite_unitaire: 1,
    prix_achat: '',
    prix_vente: '',
    code_barre: '',
  });

  // ✅ FIX : Conversion objet backend → tableau React
  useEffect(() => {
    if (stock && open) {
      setStockMinimum(stock.stock_minimum ?? 0);

      const raw = stock.conditionnements;
      if (raw && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length > 0) {
        const asArray = Object.entries(raw).map(([unite, val]) => ({
          id: Date.now() + Math.random(),
          unite,
          quantite_unitaire: val.quantite ?? 1,
          prix_achat: val.prix_achat ?? 0,
          prix_vente: val.prix_vente ?? 0,
          code_barre: val.code_barre ?? '',
        }));
        setConditionnements(asArray);
      } else {
        setConditionnements([]);
      }
    }
  }, [stock, open]);

  // Gestion conditionnements
  const handleConditionnementChange = (e) => {
    const { name, value } = e.target;
    setCurrentConditionnement(prev => ({ ...prev, [name]: value }));
  };

  const addConditionnement = () => {
    if (!currentConditionnement.unite) {
      setError("L'unité du conditionnement est obligatoire");
      return;
    }

    const existe = conditionnements.find(c => c.unite === currentConditionnement.unite);
    if (existe) {
      setError(`Un conditionnement "${currentConditionnement.unite}" existe déjà`);
      return;
    }

    const newConditionnement = {
      ...currentConditionnement,
      id: Date.now(),
      quantite_unitaire: parseFloat(currentConditionnement.quantite_unitaire) || 1,
      prix_achat: parseFloat(currentConditionnement.prix_achat) || 0,
      prix_vente: parseFloat(currentConditionnement.prix_vente) || 0,
    };

    setConditionnements(prev => [...prev, newConditionnement]);
    setCurrentConditionnement({
      unite: '',
      quantite_unitaire: 1,
      prix_achat: '',
      prix_vente: '',
      code_barre: '',
    });
    setError('');
  };

  const removeConditionnement = (index) => {
    setConditionnements(prev => prev.filter((_, i) => i !== index));
  };

  const editConditionnement = (index) => {
    const cond = conditionnements[index];
    setCurrentConditionnement(cond);
    removeConditionnement(index);
  };

  // Conversion tableau React → objet backend
  const buildConditionnementPayload = () => {
    return conditionnements.reduce((acc, cond) => {
      acc[cond.unite] = {
        quantite: cond.quantite_unitaire,
        prix_achat: parseFloat(cond.prix_achat) || 0,
        prix_vente: parseFloat(cond.prix_vente) || 0,
        code_barre: cond.code_barre || '',
      };
      return acc;
    }, {});
  };

  const handleSubmit = async () => {
    setError('');

    // ✅ Validation conditionnelle : seulement si mouvement activé
    if (avecMouvement) {
      if (!quantite) {
        setError('Veuillez saisir une quantité');
        return;
      }
      const quantiteValue = parseFloat(quantite);
      if (isNaN(quantiteValue) || quantiteValue <= 0) {
        setError('Veuillez saisir une quantité valide');
        return;
      }
      if (!motif.trim()) {
        setError('Veuillez saisir un motif');
        return;
      }
    }

    setLoading(true);

    try {
      // ✅ Appel 1 : mouvement de stock → seulement si toggle activé
      if (avecMouvement) {
        await inventoryService.ajusterStock(stock.id, {
          type_mouvement: typeMouvement,
          quantite: parseFloat(quantite),
          motif: motif.trim(),
        });
      }

      // ✅ Appel 2 : conditionnements + stock_minimum → toujours exécuté
      await inventoryService.configurerStock(stock.id, {
        stock_minimum: parseFloat(stockMinimum) || 0,
        conditionnements: buildConditionnementPayload(),
      });

      onSuccess();
      handleClose();
    } catch (err) {
      console.error('Erreur:', err);
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.message ||
        (Array.isArray(data) ? data.join(' ') : null) ||
        "Erreur lors de l'opération";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTypeMouvement('entree');
    setQuantite('');
    setMotif('');
    setStockMinimum('');
    setConditionnements([]);
    setAvecMouvement(false);
    setCurrentConditionnement({
      unite: '',
      quantite_unitaire: 1,
      prix_achat: '',
      prix_vente: '',
      code_barre: '',
    });
    setError('');
    onClose();
  };

  if (!stock) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Ajuster le stock
        <Typography variant="subtitle1" fontWeight="bold" component="p">
          {stock.produit_nom}
        </Typography>
        <Typography variant="caption" display="block" color="textSecondary" component="span">
          {stock.succursale_nom} - Code: {stock.produit_code}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>

          {/* Infos actuelles */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              Stock actuel: <strong>{stock.stock_actuel}</strong>
            </Typography>
            <Typography variant="body2">
              Stock minimum: <strong>{stock.stock_minimum}</strong>
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* ─── SECTION 1 : Conditionnements ─── */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Conditionnements & Prix
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Unité</InputLabel>
                <Select
                  value={currentConditionnement.unite}
                  onChange={(e) => {
                    handleConditionnementChange({ target: { name: 'unite', value: e.target.value } });
                    if (e.target.value === 'unite') {
                      handleConditionnementChange({ target: { name: 'quantite_unitaire', value: '1' } });
                    }
                  }}
                  label="Unité"
                >
                  <MenuItem value="unite">Unité</MenuItem>
                  <MenuItem value="caisse">Caisse</MenuItem>
                  <MenuItem value="carton">Carton</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Quantité"
                name="quantite_unitaire"
                type="number"
                value={currentConditionnement.quantite_unitaire}
                onChange={handleConditionnementChange}
                size="small"
                sx={{ width: 100 }}
                inputProps={{ step: '0.01', min: '0.01' }}
                disabled={currentConditionnement.unite === 'unite'}
              />

              <TextField
                label="Prix achat"
                name="prix_achat"
                type="number"
                value={currentConditionnement.prix_achat}
                onChange={handleConditionnementChange}
                size="small"
                sx={{ width: 130 }}
                InputProps={{ endAdornment: <InputAdornment position="end">HTG</InputAdornment> }}
                inputProps={{ step: '0.01', min: '0' }}
              />

              <TextField
                label="Prix vente"
                name="prix_vente"
                type="number"
                value={currentConditionnement.prix_vente}
                onChange={handleConditionnementChange}
                size="small"
                sx={{ width: 130 }}
                InputProps={{ endAdornment: <InputAdornment position="end">HTG</InputAdornment> }}
                inputProps={{ step: '0.01', min: '0' }}
              />

              <Tooltip title="Ajouter le conditionnement">
                <IconButton color="primary" onClick={addConditionnement}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {conditionnements.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Unité</TableCell>
                    <TableCell align="center">Quantité</TableCell>
                    <TableCell align="right">Prix Achat</TableCell>
                    <TableCell align="right">Prix Vente</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conditionnements.map((cond, index) => (
                    <TableRow key={index}>
                      <TableCell><Chip label={cond.unite} size="small" /></TableCell>
                      <TableCell align="center">{cond.quantite_unitaire}</TableCell>
                      <TableCell align="right">{cond.prix_achat} HTG</TableCell>
                      <TableCell align="right">{cond.prix_vente} HTG</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Modifier">
                          <IconButton size="small" onClick={() => editConditionnement(index)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" color="error" onClick={() => removeConditionnement(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              Aucun conditionnement défini. Le produit sera vendu à l'unité.
            </Alert>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* ─── SECTION 2 : Mouvement de stock ─── */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Mouvement de stock
            </Typography>
            {/* ✅ Toggle : activer ou désactiver le mouvement */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={avecMouvement}
                  onChange={(e) => {
                    setAvecMouvement(e.target.checked);
                    if (!e.target.checked) {
                      setQuantite('');
                      setMotif('');
                      setError('');
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color={avecMouvement ? 'primary' : 'text.secondary'}>
                  {avecMouvement ? 'Avec mouvement' : 'Sans mouvement'}
                </Typography>
              }
              labelPlacement="start"
            />
          </Box>

          {avecMouvement ? (
            <Box>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Type de mouvement</InputLabel>
                <Select
                  value={typeMouvement}
                  label="Type de mouvement"
                  onChange={(e) => setTypeMouvement(e.target.value)}
                >
                  <MenuItem value="entree">Entrée de stock</MenuItem>
                  <MenuItem value="sortie">Sortie de stock</MenuItem>
                  <MenuItem value="ajustement">Ajustement</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Quantité"
                type="number"
                value={quantite}
                onChange={(e) => setQuantite(e.target.value)}
                inputProps={{ step: '0.01', min: 0.01 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Motif"
                multiline
                rows={2}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                placeholder="Ex: Stock initial, Réapprovisionnement, Perte..."
                sx={{ mb: 1 }}
              />
            </Box>
          ) : (
            // ✅ Message informatif quand le mouvement est désactivé
            <Alert severity="info" sx={{ mb: 1 }}>
              Seuls les conditionnements et le seuil d'alerte seront mis à jour.
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          {/* ─── SECTION 3 : Seuil minimum ─── */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Seuil d'alerte
          </Typography>

          <TextField
            fullWidth
            label="Stock minimum"
            type="number"
            value={stockMinimum}
            onChange={(e) => setStockMinimum(e.target.value)}
            inputProps={{ step: '1', min: 0 }}
            helperText="Alerte quand le stock descend en dessous de ce seuil"
          />

        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Enregistrement...' : 'Confirmer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AjusterStockModal;
